import React, { Fragment, useMemo } from 'react';

import { useQuery } from '@apollo/client';
import { Box, IconArrowDown, TextButton } from '@zillow/constellation';
import { RecsCarousel } from '@zillow/home-recs-carousels';
import { DeferredRender, RenderMode } from '@zillow/react-deferred-hydration';
import { useBotState, useViewerState } from '@zillow/shopper-platform-config';

import type { getHomeRecsQuery, getOmpDataQuery } from '../../__generated__/graphql';
import { CAROUSEL_PROPERTY_LIMIT } from '../../constants/carousel';
import { CONTAINER_SX } from '../../constants/styles';
import { MORE_RECS_TEXT } from '../../constants/text';
import { useGlobalStoreState } from '../../contexts/GlobalStoreProvider';
import { homeRecsQuery, ompQuery } from '../../graphql/query';
import { AllHopsPlacements } from '../../types/genericTypes';
import { isTreatmentForTrial } from '../../utils/abs';
import { trackCollectionsData } from '../../utils/analytics';
import { isTruthy } from '../../utils/typescript';
import GatedHomeRecs from '../GatedHomeRecs/GatedHomeRecs';
import HomePageBanner from '../HomePageBanner/HomePageBanner';
import HomeRecsFooter from '../HomeRecsFooter/HomeRecsFooter';
import RTBCardList from '../RTBCards/RTBCardList';

const MORE_RECS_BELOW_RTB_ID = 'more-recs-below-rtb';
const RECS_SCROLL_OFFSET = 66;

const CAROUSEL_CONTAINER_STYLE_BASE = {
    margin: '0 auto',
    maxWidth: '1200px',
};

const hopsBelowFoldPlacements = [
    AllHopsPlacements.HOME_PAGE_RTB_CARD_1,
    AllHopsPlacements.HOME_PAGE_RTB_CARD_2,
    AllHopsPlacements.HOME_PAGE_RTB_CARD_3,
    AllHopsPlacements.HOME_PAGE_BANNER,
];

const scrollDown = () => {
    const additionalRecs =
        // eslint-disable-next-line zillow/@typescript-eslint/no-unnecessary-condition
        document &&
        (document.getElementById(MORE_RECS_BELOW_RTB_ID)?.offsetTop ?? 0) - RECS_SCROLL_OFFSET;

    if ('scrollBehavior' in document.body.style) {
        window.scroll({
            left: 0,
            top: additionalRecs,
            behavior: 'smooth',
        });
    } else {
        window.scroll(0, additionalRecs);
    }
};

const SeeMoreRecsButton = ({ isVisualRefreshEnabled }: { isVisualRefreshEnabled: boolean }) => (
    <Box
        sx={{
            display: 'flex',
            justifyContent: 'center',
            paddingTop: isVisualRefreshEnabled ? '$spacing.md' : '$spacing.sm',
        }}
    >
        <TextButton
            aria-label={MORE_RECS_TEXT}
            onClick={scrollDown}
            marginBottom={isVisualRefreshEnabled ? undefined : 'xs'}
        >
            <Box
                as={IconArrowDown}
                data-testid="arrow-down"
                marginRight="xs"
                sx={{ marginBottom: 2 }}
            />
            {MORE_RECS_TEXT}
        </TextButton>
    </Box>
);

const BelowTheFold: React.FC = () => {
    const { isLoggedIn } = useViewerState();
    const { abs, isVisualRefreshEnabled } = useGlobalStoreState();
    const isBot = useBotState();
    const { platform } = useGlobalStoreState();
    const { loading: homeRecsLoading, data: homeRecsData } = useQuery<getHomeRecsQuery>(
        homeRecsQuery,
        {
            onCompleted: (data) => {
                trackCollectionsData(data);
            },
        }
    );
    const isAddsRestricted = useMemo<boolean>(
        () =>
            !!homeRecsData?.collections?.collections?.find(
                (collection) =>
                    !!collection?.propertyDetails?.find(
                        (property) => property?.listingMetadata?.isAdsRestricted
                    )
            ),
        [homeRecsData]
    );

    const variables = {
        platform,
        isCarouselAdsRestricted: isAddsRestricted,
        surfaceId: 'rtb',
        placements: hopsBelowFoldPlacements,
    };

    const { loading: ompDataLoading, data: ompData } = useQuery<getOmpDataQuery>(ompQuery, {
        variables,
        skip: homeRecsLoading || isBot,
    });
    const [firstCollection, ...secondaryCollections] = homeRecsData?.collections?.collections ?? [];
    const trackingTag = homeRecsData?.collections?.trackingTags?.[0]?.value ?? '';

    const commonCollectionProps = {
        loggedIn: isLoggedIn,
        propertyLimit: CAROUSEL_PROPERTY_LIMIT,
        loading: homeRecsLoading,
        shouldShowHomeRecsPhotoCarousel: isTreatmentForTrial({
            abs,
            trial: 'DXP_HOME_RECS_PHOTO_CAROUSEL',
            treatment: 'ON',
        }),
        triggerLocation: 'home',
        cardSize: 'large',
        trackingTag,
    };
    return (
        <Fragment>
            {(firstCollection || homeRecsLoading) && (
                <Box
                    sx={{
                        ...(isVisualRefreshEnabled
                            ? CONTAINER_SX
                            : {
                                  ...CAROUSEL_CONTAINER_STYLE_BASE,

                                  'padding': '$spacing.md $spacing.sm $spacing.xs',

                                  '@media $md': {
                                      padding: '$spacing.xl $spacing.lg $spacing.md',
                                  },

                                  '@media $xl': {
                                      padding: '$spacing.xl $spacing.sm $spacing.md',
                                  },
                              }),
                    }}
                >
                    <RecsCarousel
                        {...commonCollectionProps}
                        // For tracking, collections are indexed starting at 1
                        collectionCount={1}
                        collection={firstCollection}
                    />
                    {!!secondaryCollections.length && (
                        <SeeMoreRecsButton isVisualRefreshEnabled={isVisualRefreshEnabled} />
                    )}
                </Box>
            )}
            {!firstCollection && !homeRecsLoading && <GatedHomeRecs />}
            <RTBCardList
                loading={(homeRecsLoading || ompDataLoading) && !isBot}
                onsiteMessageData={ompData}
            />
            <Box id={MORE_RECS_BELOW_RTB_ID} />
            {secondaryCollections.filter(isTruthy).map((collection, index) => (
                <DeferredRender
                    mode={RenderMode.RENDER_CLIENT_SIDE_ON_VISIBLE}
                    key={collection.name}
                >
                    <Box
                        sx={{
                            ...(isVisualRefreshEnabled
                                ? CONTAINER_SX
                                : {
                                      ...CAROUSEL_CONTAINER_STYLE_BASE,

                                      'padding': '$spacing.md $spacing.sm',

                                      '@media $md': {
                                          padding: '$spacing.lg $spacing.lg',
                                      },

                                      '@media $xl': {
                                          padding: '$spacing.lg $spacing.sm',
                                      },
                                  }),
                        }}
                    >
                        <RecsCarousel
                            {...commonCollectionProps}
                            // Add 2, because the first collection is already
                            // rendered above and the count is indexed from 1
                            collectionCount={index + 2}
                            collection={collection}
                        />
                    </Box>
                </DeferredRender>
            ))}
            <DeferredRender mode={RenderMode.RENDER_CLIENT_SIDE_ON_VISIBLE}>
                <HomePageBanner onsiteMessageData={ompData} />
            </DeferredRender>
            <HomeRecsFooter />
        </Fragment>
    );
};

export default BelowTheFold;
